// Pure projection of the React-owned layer state onto a live Mapbox map.
// Two entry points:
//
//   * `applyLayerState(m, layers)` — the canonical projection for the main
//     map. Walks every thematic layer and sets visibility + opacity to match
//     the supplied state, including the LULC raster/GeoJSON variant pick.
//
//   * `syncBeforeMapLayers(bm, layers)` — narrower projection for the swipe's
//     "before" map. Only the thematics that exist on the before map are
//     touched; LULC is pinned to 2024.
//
// No React, no module-level mutation. The page component schedules these
// calls; this file only knows how to *do* the projection.

import type mapboxgl from "mapbox-gl";
import type { DemoLayerStates } from "../types/agricultural-lulc";
import { setLayerVisibility } from "./map-helpers";

export function applyLayerState(
  m: mapboxgl.Map,
  targetLayers: DemoLayerStates
): void {
  applyLgaBoundary(m, targetLayers);
  applyLulc(m, targetLayers);
  applyCrops(m, targetLayers);
  applyForest(m, targetLayers);
  applyParcels(m, targetLayers);
}

function applyLgaBoundary(m: mapboxgl.Map, t: DemoLayerStates) {
  setLayerVisibility(m, "lga-boundary-fill", t.lgaBoundary.visible);
  setLayerVisibility(m, "lga-boundary-line", t.lgaBoundary.visible);
  if (m.getLayer("lga-boundary-fill")) {
    m.setPaintProperty(
      "lga-boundary-fill",
      "fill-opacity",
      t.lgaBoundary.visible ? 0.05 * t.lgaBoundary.opacity : 0
    );
  }
}

// LULC — three-way: 2024 / 2025 / change. Each variant has both a GeoJSON
// (parcel-aggregated) and a raster (10 m pixel) representation; `useRaster`
// picks which one is shown.
function applyLulc(m: mapboxgl.Map, t: DemoLayerStates) {
  const useRaster = t.lulc.useRaster === true;
  const yearStr = String(t.lulc.year);
  const geojsonForYear =
    t.lulc.year === "change" ? "lulc-change-fill" : `lulc-${yearStr}-fill`;
  const activeGeojson = useRaster ? null : geojsonForYear;
  const activeRaster = useRaster ? `lulc-raster-${yearStr}-fill` : null;

  const geojsonLayers = ["lulc-2024-fill", "lulc-2025-fill", "lulc-change-fill"];
  for (const layerId of geojsonLayers) {
    if (!m.getLayer(layerId)) continue;
    if (layerId === activeGeojson) {
      setLayerVisibility(m, layerId, t.lulc.visible);
      m.setPaintProperty(
        layerId,
        "fill-opacity",
        t.lulc.visible ? t.lulc.opacity : 0
      );
    } else {
      setLayerVisibility(m, layerId, false);
      m.setPaintProperty(layerId, "fill-opacity", 0);
    }
  }

  const rasterLayers = [
    "lulc-raster-2024-fill",
    "lulc-raster-2025-fill",
    "lulc-raster-change-fill",
  ];
  for (const layerId of rasterLayers) {
    if (!m.getLayer(layerId)) continue;
    if (layerId === activeRaster) {
      setLayerVisibility(m, layerId, t.lulc.visible);
      m.setPaintProperty(
        layerId,
        "raster-opacity",
        t.lulc.visible ? t.lulc.opacity : 0
      );
    } else {
      setLayerVisibility(m, layerId, false);
      m.setPaintProperty(layerId, "raster-opacity", 0);
    }
  }

  // Change outline only meaningful on the parcel-aggregated GeoJSON change
  // layer (raster has no parcel boundaries baked in).
  const changeOutlineVisible =
    t.lulc.visible && t.lulc.year === "change" && !useRaster;
  setLayerVisibility(m, "lulc-change-outline", changeOutlineVisible);
  if (m.getLayer("lulc-change-outline")) {
    m.setPaintProperty(
      "lulc-change-outline",
      "line-opacity",
      changeOutlineVisible ? 0.4 : 0
    );
  }
}

// Crops variant picks one of three layers: parcel-aggregated GeoJSON (winter
// 2025), the crop-class pixel raster (summer 2024), or the harvest pixel
// raster (summer 2024). Only one is visible at a time.
function applyCrops(m: mapboxgl.Map, t: DemoLayerStates) {
  const variant = t.crops.variant ?? "parcel";
  const layerByVariant: Record<string, string> = {
    parcel: "crops-2025-fill",
    crops: "crops-raster-summer2024-fill",
    harvest: "harvest-raster-summer2024-fill",
  };
  const opacityKeyByVariant: Record<string, "fill-opacity" | "raster-opacity"> = {
    parcel: "fill-opacity",
    crops: "raster-opacity",
    harvest: "raster-opacity",
  };
  for (const [variantKey, layerId] of Object.entries(layerByVariant)) {
    if (!m.getLayer(layerId)) continue;
    const show = t.crops.visible && variantKey === variant;
    setLayerVisibility(m, layerId, show);
    m.setPaintProperty(
      layerId,
      opacityKeyByVariant[variantKey],
      show ? t.crops.opacity : 0
    );
  }
}

// Forest — parcel-aggregated GeoJSON or 10 m pixel raster, picked by
// layers.forest.useRaster. Only one is visible at a time.
function applyForest(m: mapboxgl.Map, t: DemoLayerStates) {
  const useRaster = t.forest.useRaster === true;
  const showParcel = t.forest.visible && !useRaster;
  const showRaster = t.forest.visible && useRaster;
  if (m.getLayer("forest-2025-fill")) {
    setLayerVisibility(m, "forest-2025-fill", showParcel);
    m.setPaintProperty(
      "forest-2025-fill",
      "fill-opacity",
      showParcel ? t.forest.opacity : 0
    );
  }
  if (m.getLayer("forest-raster-2024-fill")) {
    setLayerVisibility(m, "forest-raster-2024-fill", showRaster);
    m.setPaintProperty(
      "forest-raster-2024-fill",
      "raster-opacity",
      showRaster ? t.forest.opacity : 0
    );
  }
}

function applyParcels(m: mapboxgl.Map, t: DemoLayerStates) {
  if (!m.getLayer("parcels-fill")) return;
  setLayerVisibility(m, "parcels-fill", t.parcels.visible);
  setLayerVisibility(m, "parcels-line", t.parcels.visible);
  m.setPaintProperty(
    "parcels-line",
    "line-opacity",
    t.parcels.visible ? t.parcels.opacity : 0
  );
}

// Mirror the user-visible thematic layers from the main map onto the before
// (left) map. LULC on the before map is always 2024 — it's the temporal
// counterpart to the main map's 2025. Forest, Crops, Parcels, and the LGA
// boundary use the exact same source/year on both sides; the swipe just
// reveals what's behind them.
export function syncBeforeMapLayers(
  bm: mapboxgl.Map,
  layers: DemoLayerStates
): void {
  const lgaVisible = layers.lgaBoundary.visible;
  setLayerVisibility(bm, "lga-boundary-fill", lgaVisible);
  setLayerVisibility(bm, "lga-boundary-line", lgaVisible);
  if (bm.getLayer("lga-boundary-fill")) {
    bm.setPaintProperty(
      "lga-boundary-fill",
      "fill-opacity",
      lgaVisible ? 0.05 * layers.lgaBoundary.opacity : 0
    );
  }

  setLayerVisibility(bm, "lulc-raster-2024-fill", layers.lulc.visible);
  if (bm.getLayer("lulc-raster-2024-fill")) {
    bm.setPaintProperty(
      "lulc-raster-2024-fill",
      "raster-opacity",
      layers.lulc.visible ? layers.lulc.opacity : 0
    );
  }

  setLayerVisibility(bm, "forest-raster-2024-fill", layers.forest.visible);
  if (bm.getLayer("forest-raster-2024-fill")) {
    bm.setPaintProperty(
      "forest-raster-2024-fill",
      "raster-opacity",
      layers.forest.visible ? layers.forest.opacity : 0
    );
  }

  const cropsVariant = layers.crops.variant ?? "crops";
  const cropLayerByVariant: Record<string, string> = {
    crops: "crops-raster-summer2024-fill",
    harvest: "harvest-raster-summer2024-fill",
  };
  for (const [variantKey, layerId] of Object.entries(cropLayerByVariant)) {
    if (!bm.getLayer(layerId)) continue;
    const show = layers.crops.visible && variantKey === cropsVariant;
    setLayerVisibility(bm, layerId, show);
    bm.setPaintProperty(layerId, "raster-opacity", show ? layers.crops.opacity : 0);
  }

  // parcels-fill is the transparent hit-detection layer; visibility must
  // track the parcels-line state so the left half of the swipe stays
  // clickable while parcels are on.
  if (bm.getLayer("parcels-fill")) {
    setLayerVisibility(bm, "parcels-fill", layers.parcels.visible);
  }
  if (bm.getLayer("parcels-line")) {
    setLayerVisibility(bm, "parcels-line", layers.parcels.visible);
    bm.setPaintProperty(
      "parcels-line",
      "line-opacity",
      layers.parcels.visible ? layers.parcels.opacity : 0
    );
  }
}
